import { useState } from "react";
import {
  DEFAULT_SETTING_REFLECTION,
  SettingsResponsePC,
  SettingsResponseReflection,
} from "@pages/data-management/data-management-types";
import InfoSvg from "@assets/icons/new/info-circle_24px.svg?react";
import { FaroBetaBadge } from "@components/common/faro-beta-badge";
import { FaroCheckbox } from "@components/common/faro-checkbox";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { Box, CircularProgress, Stack, SvgIcon } from "@mui/material";
import { FaroTextButton } from "@components/common/faro-text-button";
import reflectionsFilterGif from "@assets/reflections-filter.gif";
import { isDevModeEnabledSelector } from "@store/app/app-selector";
import { useAppSelector } from "@store/store-helper";
import { FaroText } from "@faro-lotv/flat-ui";
import { sphereColors } from "@styles/common-colors";

/** delay to reactivate the on hover effect for the reflection filter tooltip */
const REACTIVATE_TOOLTIP_DELAY = 50;

interface Props {
  /** Settings object received by getPublishSettings. */
  settingPC: SettingsResponsePC,
  /** Callback to set new value of settingPC. */
  setSettingPC: (value: React.SetStateAction<SettingsResponsePC>) => void,
  /** Settings object received by getPublishSettings. */
  settingReflection: SettingsResponseReflection,
  /** Callback to set new value of settingReflection. */
  setSettingReflection: (value: React.SetStateAction<SettingsResponseReflection>) => void,
}

/** Section with UI elements for publication settings. */
export function PublishOptions({
  settingPC,
  setSettingPC,
  settingReflection,
  setSettingReflection,
}: Props): JSX.Element {
  const isDevModeEnabled = useAppSelector(isDevModeEnabledSelector);
  const isLoading = !settingPC.modifiedAt || !settingReflection.modifiedAt;
  const [isRefFilterTooltipHidden, setIsRefFilterTooltipHidden] = useState(false);

  function setShouldGeneratePC(isEnabled: boolean): void {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- external package
    setSettingPC((prev) => ({ ...prev, value: isEnabled }));
  }
  function setShouldApplyReflection(isEnabled: boolean): void {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- external package
    setSettingReflection((prev) => ({ ...prev, value: isEnabled }));
  }
  function hideRefFilterTooltip(): void {
    setIsRefFilterTooltipHidden(true);
    /** Enable tooltip again on hover almost instantly */
    setTimeout(() => {
      setIsRefFilterTooltipHidden(false);
    }, REACTIVATE_TOOLTIP_DELAY);
  }

  const RefFiltertooltipContent = (
    <Box sx={{ padding: "10px 15px 40px 15px" }}>
      <FaroText
        variant="labelM"
        color={sphereColors.pureWhite}
        sx={{ textTransform: "uppercase" }}
      >
        reflection filter
        <FaroBetaBadge sx={{ top: "2px", float: "right" }}/>
      </FaroText>
      <Box
        mt={2}
        mb={1}
        sx={{
          textAlign: "center",
        }}
      >
        <img
          src={reflectionsFilterGif}
          alt="Reflection Filter Demo GIF"
          style={{ display: "inline-block", width: "100%" }}
        />
      </Box>
      <FaroText
        variant="bodyS"
        color={sphereColors.pureWhite}
      >
        Enabling the reflection filter may increase processing time. If there are no reflective surfaces
        (e.g. windows, mirrors), disabling it can save time and resources.
      </FaroText>
      <FaroTextButton
        sx={{ float: "right" }}
        onClick={hideRefFilterTooltip}
      >
        <FaroText
          variant="labelL"
          color={sphereColors.blue500}
        >
          Got it
        </FaroText>
      </FaroTextButton>
    </Box>
  );

  return (
    <Stack>
      <Box sx={{
        minHeight: "25px",
        marginTop: "25px",
        fontWeight: "bold",
      }}>
        <span style={{marginRight: "8px"}}>Publish Options</span>
        <CircularProgress size={20} sx={{mb: "-5px", visibility: isLoading ? "visible" : "hidden"}} />
      </Box>

      <Box sx={{ marginTop: "-10px" }}>
        <FaroCheckbox
          label="Generate point cloud"
          isDisabled={!isDevModeEnabled || isLoading}
          isChecked={!isDevModeEnabled || settingPC.value}
          onChange={() => {
            setShouldGeneratePC(!settingPC.value);
            // If PC is being enabled, and DEFAULT_SETTING_REFLECTION is true (later probably),
            // then also enable the reflection filter.
            setShouldApplyReflection(!settingPC.value && DEFAULT_SETTING_REFLECTION);
          }}
        />
      </Box>

      <Stack
        direction="row"
        sx={{
          marginLeft: "20px",
          marginTop: "-15px",
          alignItems: "baseline",
      }}>
        <FaroCheckbox
          label="Apply reflection filter"
          isDisabled={isLoading}
          isChecked={settingReflection.value}
          sx={{ marginRight: "8px" }}
          onChange={() => {
            setShouldGeneratePC(true);
            setShouldApplyReflection(!settingReflection.value);
          }}
        />

        <FaroBetaBadge sx={{ bottom: "8px", marginRight: "4px" }}/>

        <SphereTooltip
          enterDelay={0}
          shouldShowTooltip={!isRefFilterTooltipHidden}
          title={RefFiltertooltipContent}
        >
          <SvgIcon inheritViewBox component={InfoSvg} />
        </SphereTooltip>
      </Stack>
    </Stack>
  );
}
